import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { isOwnerRole, isAgencyAdminRole } from '../config/menuCodeMap';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, User as UserIcon, LogOut, Sun, Moon, CalendarDays, Building2, CalendarOff } from 'lucide-react';
import HeaderTodo from './HeaderTodo';
import patchAiIcon from '../assets/patch-ai-icon.svg';
import NotificationBell from './NotificationBell';
import SiteSwitcher from './SiteSwitcher';

/**
 * Navbar Component
 * Top navigation bar
 */

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const API_BASE = (() => {
  const raw = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  return raw.replace(/\/api\/?$/, '');
})();

const Navbar = ({ isOpen, setIsOpen, assistantOpen = false, onAskPatch }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const profileWrapRef = useRef(null);

  // Keep a live date + clock for the header chip.
  useEffect(() => {
    const getOrdinalSuffix = (d) => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    const updateTimeInfo = () => {
      const today = new Date();
      const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
      const monthName = today.toLocaleDateString('en-US', { month: 'long' });
      const date = today.getDate();

      setCurrentDate(`${dayName} ${date}${getOrdinalSuffix(date)} ${monthName}`);
    };

    updateTimeInfo();
    // Refresh the date once an hour so it rolls over at midnight.
    const id = setInterval(updateTimeInfo, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Close the profile dropdown when clicking outside.
  useEffect(() => {
    const handler = (e) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goToSettings = () => {
    setProfileOpen(false);
    navigate('/settings');
  };

  // Organisation settings — care-home (2) & domiciliary (4) managers and the
  // account owner only.
  const canOrgSettings = [2, 4].includes(Number(user?.role?.id)) || isOwnerRole(user);

  const goToOrgSettings = () => {
    setProfileOpen(false);
    navigate('/manager/organisation-settings');
  };

  // Applying for leave is personal, not a module — so it lives next to "Edit
  // profile" rather than in the sidebar, and is open to every role. Managers
  // approve their team's requests from the sidebar's Leave & Approval instead.
  const goToMyLeave = () => {
    setProfileOpen(false);
    navigate('/leave');
  };

  // A staffing agency's administrator: no leave, no to-dos, no organisation
  // settings — their agency's details live under Account instead.
  const isAgency = isAgencyAdminRole(user);
  // An agency carer signed in to a care home's dashboard for a booked shift
  // (api/utils/dashboardSession.js). The home's To-Do list isn't theirs.
  const isAgencyCarer = Boolean(user?.agency_worker);
  const goToAgencyAccount = () => {
    setProfileOpen(false);
    navigate('/agency/account');
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate('/login');
  };

  const avatarUrl = user?.avatar_url ? `${API_BASE}${user.avatar_url}` : null;
  const roleLabel = user?.job_title || user?.role?.role_name || '';

  return (
    <nav className="bg-white/90 dark:bg-[#262b33]/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-800/80 sticky top-0 z-50 transition-colors">
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 h-14">

        {/* Left Side: Greeting & Date + Mobile Menu Toggle */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 hover:bg-gray-100 dark:hover:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg transition"
          >
            {isOpen ? (
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>

          {/* Date + live clock chip — a clean, premium anchor for the bar */}
          <div className="flex items-center gap-2.5 h-9 pl-2.5 pr-3 rounded-lg bg-gray-50 dark:bg-[#31373f] border border-gray-100 dark:border-gray-800">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shrink-0">
              <CalendarDays className="w-[15px] h-[15px]" />
            </span>
            <span className="hidden sm:inline text-[13px] font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap tracking-tight">
              {currentDate}
            </span>
          </div>

          {/* Multi-site groups: which site everything on screen belongs to. */}
          <SiteSwitcher />

          {/* An agency carer working in a care home's dashboard: whose it is,
              and that it closes with their booked shift. */}
          {isAgencyCarer && (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 text-[12px] font-semibold">
              Agency shift at {user?.organization?.site_name || user?.organization?.name || 'this care home'} · access ends with your shift
            </span>
          )}
        </div>

        {/* Right Side: Notifications + Profile */}
        <div className="flex items-center gap-2 md:gap-2.5">
          {/* Ask Patch — the assistant's launcher. It used to float in the
              bottom-right corner, over whatever a page put there. The panel it
              opens is mounted by DashboardLayout, not here: this bar's
              backdrop-blur is a containing block for `fixed` children. */}
          {onAskPatch && (
            <button
              onClick={onAskPatch}
              aria-label="Ask Patch"
              aria-expanded={assistantOpen}
              title="Ask Patch — how-to and your own data"
              className="inline-flex items-center gap-1.5 h-9 pl-1.5 pr-1.5 sm:pr-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
            >
              <img src={patchAiIcon} alt="" className="h-6 w-6 rounded-md" />
              <span className="hidden sm:inline text-[13px] font-semibold whitespace-nowrap">Ask Patch</span>
            </button>
          )}

          {/* Theme toggle — compact icon button */}
          <button
            onClick={toggleTheme}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            className="inline-flex items-center justify-center h-9 w-9 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#3a4048] transition-colors"
          >
            {theme === 'light' ? (
              <Moon className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" />
            ) : (
              <Sun className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" />
            )}
          </button>

          {/* Organization To-Do popover — a care organisation's, so not for an agency. */}
          {!isAgency && !isAgencyCarer && <HeaderTodo />}

          {/* Notification Bell + dropdown */}
          <NotificationBell />

          {/* Divider separating actions from identity — premium touch */}
          <div className="hidden md:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

          {/* Profile menu — replaces the sidebar Settings entry. Clicking
              the avatar opens a small dropdown with "Edit profile" (goes
              to /settings) and "Logout". */}
          <div className="relative" ref={profileWrapRef}>
            <button
              onClick={() => setProfileOpen((v) => !v)}
              className="flex items-center gap-2 h-9 pl-1 pr-1.5 md:pr-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#31373f] transition-colors"
              title="Account"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[11px] font-black overflow-hidden ring-2 ring-white dark:ring-[#262b33] shadow-sm">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={user?.name} className="w-full h-full object-cover" />
                ) : (
                  initials(user?.name)
                )}
              </div>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-[12px] font-bold text-gray-800 dark:text-gray-200 max-w-[120px] truncate">
                  {user?.name || 'Account'}
                </span>
                {roleLabel && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-tight max-w-[120px] truncate">
                    {roleLabel}
                  </span>
                )}
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${profileOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] w-[220px] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] z-20 overflow-hidden">
                {/* Identity strip */}
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[11px] font-black overflow-hidden shrink-0">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={user?.name} className="w-full h-full object-cover" />
                    ) : (
                      initials(user?.name)
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-gray-800 dark:text-gray-200 truncate">
                      {user?.name || 'Account'}
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                      {user?.user_id ? `ID: ${user.user_id}` : ''}
                    </p>
                  </div>
                </div>

                <button
                  onClick={goToSettings}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                >
                  <UserIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  Edit profile
                </button>
                {isAgency && (
                  <button
                    onClick={goToAgencyAccount}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                  >
                    <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    Agency account
                  </button>
                )}
                {/* The account owner is not an employee and takes no leave;
                    nor is an agency administrator one of the home's staff. */}
                {!isOwnerRole(user) && !isAgency && !isAgencyCarer && (
                  <button
                    onClick={goToMyLeave}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                  >
                    <CalendarOff className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    Apply for leave
                  </button>
                )}
                {canOrgSettings && (
                  <button
                    onClick={goToOrgSettings}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                  >
                    <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    Organisation settings
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition border-t border-gray-100 dark:border-gray-800"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </nav>
  );
};

export default Navbar;
